import { computed, defineComponent, onUnmounted, ref, shallowRef, watch } from '@vue/composition-api';
import { AsyncGogoRuntime, loadGogoLib } from './runtime-loader';
import './tw-reset.scss';
import { useGogoRuntimeEnv } from './useGogoRuntimeEnv';
import { isEqual } from 'lodash';
import StatusCard from './StatusCard.vue';
import { useSetupAICheck } from './check/setup';
export default defineComponent({
    name: 'GogoRuntime',
    components: {
        AsyncGogoRuntime,
        StatusCard,
    },
    props: {
        config: { type: Object, required: true }, // { pageId, pageConfig: { artifact } }
    },
    setup(props) {
        const { markReady } = useSetupAICheck();
        const loadLib = loadGogoLib('runtime'); // 触发加载 lib
        const wujiAiPageUK = new URL(window.location.href).searchParams?.get('wujiAiPageUK');
        const isDebugMode = ref(!!wujiAiPageUK);
        const cfgFromDebug = shallowRef(null);
        const cfg = computed(() => (isDebugMode.value ? cfgFromDebug.value : props.config?.pageConfig));
        const loading = computed(() => !cfg.value);
        const debugBridge = shallowRef(null);
        let debugBridgeForEditor;
        function handleGogoHostReady(controller) {
            markReady(loadLib);
            if (debugBridge.value) {
                // 下面逻辑只给调试模式
                debugBridgeForEditor = controller.getDebugBridgeForEditor();
                controller.setDebugBridge(debugBridge.value);
            }
        }
        const currentExternals = ref([]);
        watch(() => props.config.pageConfig?.artifact?.externals, (val) => {
            if (Array.isArray(val))
                currentExternals.value = val.slice();
        }, { immediate: true });
        watch(() => cfg.value?.artifact?.externals, (val) => {
            if (Array.isArray(val) && !isEqual(val, currentExternals.value))
                currentExternals.value = val.slice();
        }, { immediate: true });
        function handleDebugUserCodeChanged({ content, pageConfigExtra }) {
            const userCode = content;
            console.log('[wuji-gogo] handleDebugUserCodeChanged', userCode, pageConfigExtra);
            cfgFromDebug.value = {
                ...pageConfigExtra,
                artifact: userCode,
            };
            if (userCode.externals && !isEqual(userCode.externals, currentExternals.value)) {
                currentExternals.value = userCode.externals.slice();
            }
        }
        if (wujiAiPageUK && isDebugMode.value) {
            // 调试模式的
            const channel = new MessageChannel();
            const port = channel.port1;
            port.onmessage = (e) => {
                if (!isDebugMode.value)
                    return; // 已经关闭了
                if (connectionTimeoutTimer) {
                    clearTimeout(connectionTimeoutTimer);
                    connectionTimeoutTimer = null;
                }
                if (e.data.type === '__preview-data__') {
                    cfgFromDebug.value = e.data.data;
                    return;
                }
                if (e.data.type === '__debug-bridge_call_from_editor__') {
                    const { method, args } = e.data;
                    const port = e.data.port;
                    Promise.resolve()
                        .then(() => debugBridgeForEditor?.[method](...args))
                        .then(result => port.postMessage({ result }), error => port.postMessage({ error }));
                    return;
                }
                if (e.data.type === '__preview-bad-request__') {
                    closeDebugConnection();
                    return;
                }
                if (e.data.type === '__preview-close__') {
                    // 还是调试模式，但是断开链接
                    debugBridge.value = null;
                    return;
                }
            };
            port.start();
            let connectionTimeoutTimer = setTimeout(() => {
                closeDebugConnection();
            }, 3000);
            function closeDebugConnection() {
                if (!isDebugMode.value)
                    return;
                isDebugMode.value = false;
                debugBridge.value = null;
                if (connectionTimeoutTimer) {
                    clearTimeout(connectionTimeoutTimer);
                    connectionTimeoutTimer = null;
                }
                port.close();
            }
            debugBridge.value = new Proxy(Object.freeze({}), // Vue 2 真是太好了
            {
                get(_, action) {
                    return function (...args) {
                        return new Promise((resolve, reject) => {
                            const channel = new MessageChannel();
                            channel.port1.onmessage = (e) => {
                                const { result, error } = e.data;
                                if (error)
                                    reject(error);
                                else
                                    resolve(result);
                                channel.port1.close();
                            };
                            channel.port1.start();
                            port?.postMessage({ type: '__debug-bridge__', action, args, port: channel.port2 }, [channel.port2]);
                        });
                    };
                },
            });
            const sendClosed = () => port?.postMessage({ type: '__preview-close__' });
            window.addEventListener('beforeunload', sendClosed);
            onUnmounted(() => {
                sendClosed();
                window.removeEventListener('beforeunload', sendClosed);
            });
            console.log('connecting preview with wujiAiPageUK', wujiAiPageUK, 'pageId', props.config.pageId);
            (window.opener || window.parent || window.top)?.postMessage({
                type: '__preview-iframe-ready__',
                key: wujiAiPageUK,
                pageId: props.config.pageId,
                port: channel.port2,
            }, '*', [channel.port2]);
        }
        const runtimeEnv = useGogoRuntimeEnv(computed(() => ({
            externals: currentExternals.value || [],
            collectionRefs: cfg.value?.collectionRefs || [],
            projectId: props.config.projectId,
            pageId: props.config.pageId,
        })));
        const allowPicking = window.location.search?.includes('iframe=1');
        return {
            loading,
            isDebugMode,
            userData: computed(() => cfg.value?.artifact),
            getGlobals: computed(() => runtimeEnv.getGlobals),
            customRequire: computed(() => runtimeEnv.customRequire),
            isExternalsLoading: runtimeEnv.isExternalsLoading,
            externalsLoadError: runtimeEnv.externalsLoadError,
            handleGogoHostReady,
            handleDebugUserCodeChanged,
            allowPicking,
        };
    },
});
